/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Input, Table as TableAnt} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useRef, useState} from 'react';

// import InformativeText from '../../constants/InformativeText';
// import {SampleExcel} from '../../constants/SampleExcel';
import {
  OPKey,
  OPLabel
} from '../../pages/configurator/orgFacility/orgInfo/constants/orgProfileKeyLabel';
import BusinessDetailsForm from '../../pages/configurator/orgFacility/orgInfo/utils/BusinessDetailsForm';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import businessDetailsAPI from '../../services/modules/Configurator/businessDetailsAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  OwnerShipKey,
  OwnerShipLabel
} from '../Forms/Constants/IP/OwnerShipKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {createBulkRowSelection} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';

const BusinessDetailsTable: React.FC<{}> = () => {
  interface DataType {
    _id: string;
    year: string;
    totalFloorSpace: number;
    totalNumberOfEmployees: number;
    totalNoOfVehicle: number;
    totalRevenue: number;
    equityShare: number;
    operationalShare: number;
    financialControl: number;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const {area, currency} = useCompanyStore.getState().preUnits;

  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);

  const businessDetailsKeyLabel = [
    {
      key: OPKey.BUSINESS_YEAR,
      label: OPLabel.BUSINESS_YEAR,
      type: objTypes.string
    },
    {
      key: OPKey.TOTAL_FLOOR_SPACE,
      label: OPLabel.TOTAL_FLOOR_SPACE,
      type: objTypes.number,
      unit: area
    },
    {
      key: OPKey.TOTAL_EMP,
      label: OPLabel.TOTAL_EMP,
      type: objTypes.number
    },
    {
      key: OPKey.FLEET_NUM,
      label: OPLabel.FLEET_NUM,
      type: objTypes.number
    },
    {
      key: OPKey.TOTAL_REVENUE,
      label: OPLabel.TOTAL_REVENUE,
      type: objTypes.number,
      unit: currency,
      isCost: true
    },
    {
      key: OwnerShipKey.EQUITY_SHARES,
      label: OwnerShipLabel.EQUITY_SHARES,
      type: objTypes.percentage
    },
    {
      key: OwnerShipKey.OP_CONTROL,
      label: OwnerShipLabel.OP_CONTROL,
      type: objTypes.percentage
    },
    {
      key: OwnerShipKey.FIN_CONTROL,
      label: OwnerShipLabel.FIN_CONTROL,
      type: objTypes.percentage
    }
  ];

  useEffect(() => {
    setIsRefresh(true);
    businessDetailsAPI(APITypeEnum.GET_ALL, undefined)
      .then((res) => {
        setDataSource(
          res.map((item: any) => {
            const {operationalShare, ...rest} = item;

            return rest;
          }) ?? []
        );
      })
      .catch(() => {
        setDataSource([]);
      })
      .finally(() => {
        setIsRefresh(false);
        setRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      businessDetailsAPI(APITypeEnum.GET_ALL, undefined)
        .then((res) => {
          setDataSource(
            res.map((item: any) => {
              const {operationalShare, ...rest} = item;

              return rest;
            }) ?? []
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    businessDetailsAPI(APITypeEnum.DELETE_ALL, {
      businessDetails: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record?.[dataIndex]
          ?.toString()
          ?.toLowerCase()
          ?.includes(value?.toString()?.toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = [
    {
      title: OPLabel.BUSINESS_YEAR,
      dataIndex: OPKey.BUSINESS_YEAR,
      ...SearchFilter(
        OPLabel.BUSINESS_YEAR,
        OPKey.BUSINESS_YEAR as keyof DataType
      ),
      render: (value) => {
        return <b>{value}</b>;
      }
    },
    {
      title: OPLabel.TOTAL_FLOOR_SPACE,
      dataIndex: OPKey.TOTAL_FLOOR_SPACE,
      ...SearchFilter(
        OPLabel.TOTAL_FLOOR_SPACE,
        OPKey.TOTAL_FLOOR_SPACE as keyof DataType
      ),
      ...sorter(OPKey.TOTAL_FLOOR_SPACE as keyof DataType),
      render: (value) => {
        return value ? `${formatIndian(value)} ${area}` : '-';
      }
    },
    {
      title: OPLabel.TOTAL_EMP,
      dataIndex: OPKey.TOTAL_EMP,
      //   width: '120px',
      editable: true,
      type: 'number',
      ...SearchFilter(OPLabel.TOTAL_EMP, OPKey.TOTAL_EMP as keyof DataType),
      ...sorter(OPKey.TOTAL_EMP as keyof DataType),
      render: (value) => {
        return formatIndian(value);
      }
    },
    {
      title: OPLabel.FLEET_NUM,
      dataIndex: OPKey.FLEET_NUM,
      //   width: '120px',
      type: 'number',
      ...SearchFilter(OPLabel.FLEET_NUM, OPKey.FLEET_NUM as keyof DataType),
      ...sorter(OPKey.FLEET_NUM as keyof DataType),
      render: (value) => {
        return formatIndian(value);
      }
    },
    {
      title: OPLabel.TOTAL_REVENUE,
      dataIndex: OPKey.TOTAL_REVENUE,
      ...SearchFilter(
        OPLabel.TOTAL_REVENUE,
        OPKey.TOTAL_REVENUE as keyof DataType
      ),
      ...sorter(OPKey.TOTAL_REVENUE as keyof DataType),

      render: (value) => {
        return value ? `${currency} ${formatIndian(value)}` : '-';
      }
      //   width: '120px',
    },
    {
      title: OwnerShipLabel.EQUITY_SHARES,
      dataIndex: OwnerShipKey.EQUITY_SHARES,
      ...SearchFilter(
        OwnerShipLabel.EQUITY_SHARES,
        OwnerShipKey.EQUITY_SHARES as keyof DataType
      ),
      ...sorter(OwnerShipKey.EQUITY_SHARES as keyof DataType),

      render: (value) => {
        return `${formatIndian(value)} ${value ? '%' : ''}`;
      }
    },
    {
      title: OwnerShipLabel.OP_CONTROL,
      dataIndex: OwnerShipKey.OP_CONTROL,
      ...SearchFilter(
        OwnerShipLabel.OP_CONTROL,
        OwnerShipKey.OP_CONTROL as keyof DataType
      ),
      ...sorter(OwnerShipKey.OP_CONTROL as keyof DataType),

      render: (value) => {
        return `${formatIndian(value)} ${value ? '%' : ''}`;
      }
    },
    {
      title: OwnerShipLabel.FIN_CONTROL,
      dataIndex: OwnerShipKey.FIN_CONTROL,
      ...SearchFilter(
        OwnerShipLabel.FIN_CONTROL,
        OwnerShipKey.FIN_CONTROL as keyof DataType
      ),

      ...sorter(OwnerShipKey.FIN_CONTROL as keyof DataType),

      render: (value) => {
        return `${formatIndian(value)} ${value ? '%' : ''}`;
      }
    },
    ...(!hasUserRole(UserRole.View_Only)
      ? ([
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '',
            fixed: 'right',
            width: '200px',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  isConfigurator={true}
                />
              ) : null
          }
        ] as any)
      : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Business Details"
        filter={{facilityId: '', selectedYear: ''}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Business Details'
        }}
        tooltip={''}
        sampleDownload={{href: ''}}
        jobs={{
          setShowJobs,
          uploadType: ''
        }}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        isTable={false}
        showAddOnly={true}
        shouldEdit={!hasUserRole(UserRole.View_Only)}
        onSelectAll={
          !hasUserRole(UserRole.View_Only) ? handleSelectAll : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
      />
      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          }
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.AssetFranchise}
        details={''}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={<BusinessDetailsForm />}
        API={businessDetailsAPI}
        buttonDisabled={{value: submitDisabled, setter: setSubmitDisabled}}
        stepsTitle={['Business Details']}
        formPreview={businessDetailsKeyLabel}
        isConfigurator={true}
      />
    </div>
  );
};

export default BusinessDetailsTable;
